import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, EyeOff, Eye, ExternalLink, Users, Image as ImageIcon, Layers, MapPin, RefreshCw } from 'lucide-react';
import { adminGetCatalogue, adminSetValidation, adminImportPreview } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const VAL = {
  valide: { label: 'Validé', cls: 'bg-green-600' },
  a_verifier: { label: 'À vérifier', cls: 'bg-amber-500' },
  en_attente: { label: 'En attente', cls: 'bg-slate-500' },
  masque: { label: 'Masqué', cls: 'bg-red-600' },
};

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('fr-FR'); } catch (e) { return iso; } };

const CatalogueManager = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const { toast } = useToast();

  const load = async () => { try { setData(await adminGetCatalogue()); } catch (e) { setData({ items: [], counts: {} }); } };
  useEffect(() => { load(); }, []);

  const setVal = async (sourceId, validation) => {
    setBusy(sourceId);
    try { await adminSetValidation(sourceId, validation); await load(); }
    catch (e) { toast({ title: 'Erreur', description: 'Mise à jour impossible.' }); }
    finally { setBusy(null); }
  };

  const validateAll = async () => {
    setImporting(true); setReport(null);
    try {
      const r = await adminImportPreview();
      setReport(r);
      toast({ title: 'Simulation d\u2019import complet', description: `${r.a_valider} à publier, ${r.ignores_a_verifier} à vérifier, ${r.masques} masqué(s). Aucun contenu publié.` });
    } catch (e) { toast({ title: 'Échec', description: 'Simulation impossible.' }); }
    finally { setImporting(false); }
  };

  const items = useMemo(() => {
    if (!data) return [];
    return filter === 'all' ? data.items : data.items.filter((x) => x.validation === filter);
  }, [data, filter]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const c = data.counts || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[['all', `Tous (${c.total || 0})`], ['valide', `Validés (${c.valide || 0})`], ['a_verifier', `À vérifier (${c.a_verifier || 0})`], ['en_attente', `En attente (${c.en_attente || 0})`], ['masque', `Masqués (${c.masque || 0})`]].map(([k, lbl]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm font-800 transition-colors ${filter === k ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>{lbl}</button>
          ))}
        </div>
        <button onClick={validateAll} disabled={importing} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 text-white font-800 text-sm hover:scale-105 transition-transform disabled:opacity-60">
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Import complet (simulation)
        </button>
      </div>

      {report && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-display font-700 mb-2">Simulation d'import complet <span className="text-xs font-700 text-amber-600">(aucun contenu publié)</span></h3>
          <div className="flex flex-wrap gap-3 text-sm font-700">
            <span className="text-muted-foreground">Total : {report.total}</span>
            <span className="text-green-600">Déjà validés : {report.deja_valides}</span>
            <span className="text-primary">À publier : {report.a_valider}</span>
            <span className="text-amber-500">Ignorés (à vérifier) : {report.ignores_a_verifier}</span>
            <span className="text-red-600">Masqués : {report.masques}</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((e) => {
          const needsReview = e.validation === 'a_verifier';
          return (
            <div key={e.source_id} className={`bg-card border rounded-2xl p-3 flex gap-4 ${needsReview ? 'border-amber-400' : 'border-border'}`}>
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0 relative">
                {e.featured_image?.url ? <img src={e.featured_image.url} alt={e.titre} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-muted-foreground" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-800 text-white uppercase ${VAL[e.validation]?.cls || 'bg-muted-foreground'}`}>{VAL[e.validation]?.label || e.validation}</span>
                  <span className="text-[11px] font-700 text-muted-foreground">source_id: {e.source_id}</span>
                  {needsReview && <span className="text-[11px] font-800 text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {!e.featured_image?.url ? 'sans vignette' : e.images_count === 0 ? 'sans image' : 'sans auteur'}</span>}
                </div>
                <p className="font-display font-700 leading-tight truncate">{e.titre}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground font-600 mt-1">
                  <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {e.images_count} img</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {(e.auteurs || []).map((a) => a.name).join(', ') || '—'}</span>
                  {(e.collections || []).length > 0 && <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {e.collections.map((x) => x.name).join(', ')}</span>}
                  {(e.locations || []).length > 0 && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.locations.map((x) => x.name).join(', ')}</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Sync : {fmt(e.synced_at)} · {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer" className="text-primary font-700 inline-flex items-center gap-0.5">Source <ExternalLink className="w-3 h-3" /></a>}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                {busy === e.source_id ? <Loader2 className="w-5 h-5 animate-spin text-primary m-auto" /> : (<>
                  {e.validation !== 'valide' && <button onClick={() => setVal(e.source_id, 'valide')} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-600 text-white font-800 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Valider</button>}
                  {e.validation === 'valide' && <a href={`/toon/${e.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground font-800 text-xs"><Eye className="w-3.5 h-3.5" /> Lire</a>}
                  <button onClick={() => setVal(e.source_id, 'masque')} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-muted text-foreground font-800 text-xs hover:bg-destructive hover:text-white transition-colors"><EyeOff className="w-3.5 h-3.5" /> Masquer</button>
                </>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CatalogueManager;
